import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { AddNewEventButton, PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import {
  BEGINNING_OF_TIME,
  selectCurrentFarm,
  useAppDispatch,
  useAppSelector,
} from "model";
import { getMetabaseUrl } from "model/metabaseUrlSlice";
import { useEffect, useState } from "react";
import styled from "styled-components";
import CombinedInputsOutputsList, {
  EventType,
  useCombinedEvents,
} from "./CombinedInputsOutputsList";
import EventFilterButton from "./EventFilterButton";
import ExportButton from "./ExportButton";

export default function CombinedInputsOutputsDetail() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const currentFarm = useAppSelector(selectCurrentFarm);

  const [feedStockMetabaseUrl, setFeedStockMetabaseUrl] = useState<string>("");
  const [yieldSummaryMetabaseUrl, setYieldSummaryMetabaseUrl] =
    useState<string>("");
  const [selectedEventTypes, setSelectedEventTypes] = useState<EventType[]>([]);
  const locale = useAppSelector((state) => state.app.locale);

  // Get filtered events for export
  const filteredEvents = useCombinedEvents(selectedEventTypes);

  const handleFilterChange = (types: EventType[]) => {
    setSelectedEventTypes(types);
  };

  // Fetch Feed Stock Summary iframe URL
  useEffect(() => {
    if (!feedStockMetabaseUrl) {
      const fetchFeedStockUrl = async () => {
        const params: { [name: string]: string } = {};
        params["date_filter"] = "2020-07-24T14:15:22.000Z";
        params["farm_guid"] = currentFarm?.guid ?? "";
        try {
          const res = await dispatch(
            getMetabaseUrl({
              visualizationId: "AVAILABLE_STOCK_SUMMARY",
              params,
            })
          ).then(unwrapResult);
          setFeedStockMetabaseUrl(res.url);
        } catch (e) {
          console.log(e);
        }
      };
      void fetchFeedStockUrl();
    }
  }, [currentFarm?.guid, feedStockMetabaseUrl, dispatch]);

  // Fetch Yield Summary iframe URL
  useEffect(() => {
    if (!yieldSummaryMetabaseUrl) {
      const fetchYieldSummaryUrl = async () => {
        const params: { [name: string]: string } = {};
        params["date_filter"] = BEGINNING_OF_TIME;
        params["farm_guid"] = currentFarm?.guid ?? "";
        try {
          const res = await dispatch(
            getMetabaseUrl({
              visualizationId: "YIELD_SUMMARY",
              params,
            })
          ).then(unwrapResult);
          setYieldSummaryMetabaseUrl(res.url);
        } catch (e) {
          console.log(e);
        }
      };
      void fetchYieldSummaryUrl();
    }
  }, [currentFarm?.guid, yieldSummaryMetabaseUrl, dispatch]);

  return (
    <>
      <PageTitleBar title={t("inputs.outputs.combined.title")} showDivider />

      {/* Main Content */}
      <FieldEventListContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            py: 3,
          }}
        >
          {/* Header Section */}
          {isMobile && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                px: 3,
                py: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <AddNewEventButton maxWidth="150px" />
              </Box>
            </Box>
          )}

          {/* Feed Stock Summary and Yield Summary iframes */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 3,
              px: 3,
              py: 0,
              width: "100%",
            }}
          >
            {/* Yield Summary Card */}
            {yieldSummaryMetabaseUrl && (
              <SummaryCard>
                <iframe
                  frameBorder="0"
                  src={yieldSummaryMetabaseUrl}
                  width="100%"
                  height="100%"
                  title="Yield-Summary"
                  style={{
                    border: "none",
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </SummaryCard>
            )}

            {/* Feed in Store Card */}
            {feedStockMetabaseUrl && (
              <SummaryCard>
                <iframe
                  frameBorder="0"
                  src={feedStockMetabaseUrl}
                  width="100%"
                  height="100%"
                  title="Feed-in-Store"
                  style={{
                    border: "none",
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </SummaryCard>
            )}
          </Box>
        </Box>
      </FieldEventListContainer>

      {/* Combined events list */}
      <FieldEventListContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            py: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              px: 3,
            }}
          >
            <EventFilterButton
              selectedTypes={selectedEventTypes}
              onFilterChange={handleFilterChange}
            />
            <ExportButton events={filteredEvents} locale={locale} />
          </Box>
          <CombinedInputsOutputsList selectedEventTypes={selectedEventTypes} />
        </Box>
      </FieldEventListContainer>
    </>
  );
}

const FieldEventListContainer = styled(Container)`
  flex-grow: 1;
`;

const SummaryCard = styled(Box)`
  position: relative;
  border: 0.92px solid #e4e4e7;
  border-radius: 13.798px;
  height: 314.594px;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  background-color: #ffffff;

  @media (max-width: 600px) {
    width: 100%;
    flex: 1 1 auto;
  }
`;
